import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Copy, Trash2, Save, X, Upload, Image, Maximize2, Home, Scissors, Brain, Bone, Heart, User, Droplet, Palette, ClipboardList, RotateCw, FlipHorizontal, ArrowUp, ArrowDown, Package, Ear, Eye, Activity, ChevronLeft, ChevronRight, FolderOpen, Grid } from 'lucide-react';
import { api } from './api';
import PickingCards from './PickingCards';
import { AuthProvider, useAuth, ProtectedRoute } from './AuthContext';
import { set401Handler } from './api';
import LoginPage from './LoginPage';
import AdminPage from './AdminPage';
import { Shield } from 'lucide-react';

const SPECIALTIES = [
  { name: 'Cardiovascular', icon: Heart, color: 'from-red-400 to-rose-600', isPage: true },
  { name: 'Cysto', icon: Droplet, color: 'from-blue-400 to-blue-600' },
  { name: 'ENT', icon: Ear, color: 'from-amber-400 to-yellow-600', isPage: true },
  { name: 'General', icon: Scissors, color: 'from-green-400 to-green-600' },
  { name: 'GYN', icon: User, color: 'from-pink-400 to-pink-600' },
  { name: 'Neuro', icon: Brain, color: 'from-purple-400 to-purple-600' },
  { name: 'Ophthalmology', icon: Eye, color: 'from-cyan-400 to-sky-600', isPage: true },
  { name: 'Ortho', icon: Bone, color: 'from-orange-400 to-orange-600' },
  { name: 'Plastics', icon: Palette, color: 'from-rose-400 to-rose-600' },
  { name: 'Urology', icon: Activity, color: 'from-indigo-400 to-violet-600', isPage: true },
  { name: 'Vascular', icon: Heart, color: 'from-red-400 to-red-600' }
];

function compressImage(dataUrl, maxPx, quality) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      const ratio = Math.min(maxPx / img.width, maxPx / img.height, 1);
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      const isPng = dataUrl.startsWith('data:image/png');
      if (!isPng) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
      }
      ctx.drawImage(img, 0, 0, w, h);
      resolve(isPng ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = (err) => reject(err);
    img.src = dataUrl;
  });
}

// Navigation Bar Component
function NavBar({ currentPage, onNavigate, role, user, onLogout }) {
  const navItems = [
    { key: 'home', label: 'Home', icon: Home },
    { key: 'cases', label: 'Case Cards', icon: Scissors },
    { key: 'picking', label: 'Picking Cards', icon: ClipboardList },
    { key: 'materials', label: 'Materials', icon: Package },
    ...(role === 'admin' ? [{ key: 'admin', label: 'Admin', icon: Shield }] : [])
  ];

  return (
    <nav className="bg-slate-900 border-b border-slate-800">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.key ||
                (item.key === 'cases' && ['cases', 'cardiovascular', 'ent', 'ophthalmology', 'urology'].includes(currentPage));
              return (
                <button
                  key={item.key}
                  onClick={() => onNavigate(item.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-slate-700 text-teal-400'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
                  }`}
                >
                  <Icon size={18} />
                  {item.label}
                </button>
              );
            })}
          </div>
          {user && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-400">
                <span className="font-medium text-slate-100">{user.username}</span>
                <span className="ml-1 text-xs text-slate-500 capitalize">({user.role})</span>
              </span>
              <button
                onClick={onLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:bg-slate-800 hover:text-slate-100 rounded-lg transition-colors"
              >
                <User size={15} />
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

function App() {
  const { user, role, logout, loading } = useAuth();

  React.useEffect(() => {
    set401Handler(() => logout());
    return () => set401Handler(null);
  }, [logout]);

  const [cases, setCases] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentCase, setCurrentCase] = useState(null);
  const [pendingCaseId, setPendingCaseId] = useState(() => {
    try {
      const saved = localStorage.getItem('currentCase');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed?.id || null;
      }
    } catch (e) {
      localStorage.removeItem('currentCase');
    }
    return null;
  });
  const [selectedSpecialty, setSelectedSpecialty] = useState(() => {
    return localStorage.getItem('selectedSpecialty') || null;
  });
  const [showHome, setShowHome] = useState(() => {
    const saved = localStorage.getItem('showHome');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [draggingImage, setDraggingImage] = useState(null);
  const [resizingImage, setResizingImage] = useState(null);
  const [rotatingImage, setRotatingImage] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [rotateStart, setRotateStart] = useState({ angle: 0, startAngle: 0 });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSavedToast, setShowSavedToast] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => {
    return localStorage.getItem('currentPage') || 'home';
  });
  const [currentCanvasPage, setCurrentCanvasPage] = useState(1);
  const [totalCanvasPages, setTotalCanvasPages] = useState(2);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [imageLibrary, setImageLibrary] = useState([]);
  const [libraryCategories, setLibraryCategories] = useState(['General']);
  const [selectedLibraryCategory, setSelectedLibraryCategory] = useState(null);
  const [draggingLibraryImage, setDraggingLibraryImage] = useState(null);

  // Persist state to localStorage
  useEffect(() => {
    localStorage.setItem('currentPage', currentPage);
  }, [currentPage]);

  useEffect(() => {
    localStorage.setItem('selectedSpecialty', selectedSpecialty || '');
  }, [selectedSpecialty]);

  useEffect(() => {
    localStorage.setItem('showHome', JSON.stringify(showHome));
  }, [showHome]);

  // Browser history management for back button
  useEffect(() => {
    const handlePopState = (event) => {
      const state = event.state;
      if (state) {
        if (state.currentCase) {
          // Find the full case from cases array
          const fullCase = cases.find(c => c.id === state.currentCase);
          if (fullCase) {
            setCurrentCase(fullCase);
            setTotalCanvasPages(countCanvasPages(fullCase));
            setCurrentCanvasPage(1);
          }
        } else {
          setCurrentCase(null);
        }
        setCurrentPage(state.currentPage || 'home');
        setSelectedSpecialty(state.selectedSpecialty || null);
        setShowHome(state.showHome !== undefined ? state.showHome : true);
      } else {
        // No state, go to home
        setCurrentCase(null);
        setCurrentPage('home');
        setSelectedSpecialty(null);
        setShowHome(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [cases]);

  // Push state when navigation changes
  useEffect(() => {
    const state = {
      currentPage,
      selectedSpecialty,
      showHome,
      currentCase: currentCase?.id || null
    };
    const url = currentCase ? `#case-${currentCase.id}` :
                selectedSpecialty ? `#${selectedSpecialty.toLowerCase()}` :
                currentPage !== 'home' ? `#${currentPage}` : '#';

    // Only push if this is a new navigation (not from popstate)
    if (window.history.state?.currentCase !== state.currentCase ||
        window.history.state?.currentPage !== state.currentPage ||
        window.history.state?.selectedSpecialty !== state.selectedSpecialty) {
      window.history.pushState(state, '', url);
    }
  }, [currentCase, currentPage, selectedSpecialty, showHome]);

  useEffect(() => {
    if (currentCase) {
      // Store only essential info without images to avoid localStorage quota exceeded
      const caseForStorage = {
        id: currentCase.id,
        surgeon: currentCase.surgeon,
        procedure: currentCase.procedure,
        specialty: currentCase.specialty,
        date: currentCase.date
      };
      try {
        localStorage.setItem('currentCase', JSON.stringify(caseForStorage));
      } catch (e) {
        console.warn('Could not save to localStorage:', e);
      }
    } else {
      localStorage.removeItem('currentCase');
    }
  }, [currentCase]);

  useEffect(() => {
    loadCases();
  }, []);

  // Load full case from database if we have a pending case ID
  useEffect(() => {
    if (pendingCaseId && cases.length > 0) {
      const fullCase = cases.find(c => c.id === pendingCaseId);
      if (fullCase) {
        setCurrentCase(fullCase);
        setTotalCanvasPages(countCanvasPages(fullCase));
        setCurrentCanvasPage(1);
      }
      setPendingCaseId(null);
    }
  }, [pendingCaseId, cases]);

  const loadCases = async () => {
    try {
      const loadedCases = await api.getCases();
      setCases(loadedCases);
    } catch (error) {
      console.error('Error loading cases:', error);
    }
  };

  const loadImageLibrary = async (category = null) => {
    try {
      const images = await api.getImageLibrary(category);
      setImageLibrary(images);
      const categories = await api.getImageCategories();
      setLibraryCategories(['All', ...categories]);
    } catch (error) {
      console.error('Error loading image library:', error);
    }
  };

  const handleUploadToLibrary = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const raw = event.target.result;
        const [compressedSrc, compressedThumb] = await Promise.all([
          compressImage(raw, 800, 0.75),
          compressImage(raw, 160, 0.6),
        ]);
        const img = new window.Image();
        img.onload = async () => {
          const imageData = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            name: file.name.replace(/\.[^/.]+$/, ''),
            category: selectedLibraryCategory || 'General',
            src: compressedSrc,
            thumbnail: compressedThumb,
            width: img.width,
            height: img.height,
          };
          try {
            await api.uploadToLibrary(imageData);
            loadImageLibrary(selectedLibraryCategory);
          } catch (error) {
            console.error('Error uploading image:', error);
          }
        };
        img.onerror = (err) => {
          console.error('Failed to decode compressed image:', err);
        };
        img.src = compressedSrc;
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleDeleteLibraryImage = async (imageId) => {
    if (window.confirm('Delete this image from library?')) {
      try {
        await api.deleteLibraryImage(imageId);
        loadImageLibrary(selectedLibraryCategory);
      } catch (error) {
        console.error('Error deleting image:', error);
      }
    }
  };

  const handleLibraryDragStart = (e, image) => {
    setDraggingLibraryImage(image);
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: image.id, width: image.width, height: image.height }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleCanvasDrop = async (e) => {
    e.preventDefault();
    setDraggingLibraryImage(null);
    const canvas = document.getElementById('surgical-canvas');
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left - 75;
    const y = e.clientY - rect.top - 75;

    try {
      const imageData = JSON.parse(e.dataTransfer.getData('text/plain'));
      const { src } = await api.getLibraryImageSrc(imageData.id);
      const newImage = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        src,
        x: Math.max(10, Math.min(640, x)),
        y: Math.max(10, Math.min(440, y)),
        width: imageData.width || 150,
        height: imageData.height || 150,
        zIndex: (currentCase[getImageField(currentCanvasPage)] || []).length + 1,
        rotation: 0,
        mirrored: false
      };

      const imageField = getImageField(currentCanvasPage);
      setCurrentCase(prev => ({
        ...prev,
        [imageField]: [...(prev[imageField] || []), newImage]
      }));
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Error dropping image:', error);
    }
  };

  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Helper function to get image field name for a canvas number
  const getImageField = (canvasNumber) => {
    if (canvasNumber === 1) return 'images';
    return `images${canvasNumber}`;
  };

  // Helper function to count total canvas pages in a case
  const countCanvasPages = (caseData) => {
    let count = 1; // Always at least 1 page
    if (caseData.images2 && caseData.images2.length > 0) count = 2;
    for (let i = 3; i <= 10; i++) {
      if (caseData[`images${i}`] && caseData[`images${i}`].length > 0) {
        count = i;
      }
    }
    return Math.max(count, 2); // Minimum 2 pages
  };

  const saveCase = async (caseData) => {
    await api.saveCase(caseData);
    await loadCases();
  };

  const deleteCase = async (caseId) => {
    if (currentCase?.id === caseId) {
      setCurrentCase(null);
    }
    try {
      await api.deleteCase(caseId);
      await loadCases();
    } catch (error) {
      console.error('Error deleting case:', error);
    }
  };

  const handleCreateCase = (specialty) => {
    const newCase = {
      id: Date.now().toString(),
      specialty: specialty,
      surgeon: 'New Surgeon',
      procedure: 'New Procedure',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      images: [],
      images2: [],
      sutures: [],
      trays: [],
      gloves: [],
      meds: [],
      drapes: [],
      dressings: [],
      supplies: []
    };
    setCurrentCase(newCase);
    setTotalCanvasPages(2);
    setCurrentCanvasPage(1);
    setShowHome(false);
    setSelectedSpecialty(null);
    setHasUnsavedChanges(true);
  };

  const handleCopyCase = (caseData) => {
    // Generate new IDs for images to avoid conflicts
    const copyImages = (images) => (images || []).map(img => ({
      ...img,
      id: Date.now().toString() + Math.random()
    }));

    const newCase = {
      ...caseData,
      id: Date.now().toString(),
      surgeon: caseData.surgeon + ' (Copy)',
      date: new Date().toISOString().split('T')[0],
      notes: caseData.notes || '',
      images: copyImages(caseData.images),
      sutures: (caseData.sutures || []).map(item => ({ ...item, id: Date.now().toString() + Math.random() })),
      trays: (caseData.trays || []).map(item => ({ ...item, id: Date.now().toString() + Math.random() })),
      gloves: (caseData.gloves || []).map(item => ({ ...item, id: Date.now().toString() + Math.random() })),
      meds: (caseData.meds || []).map(item => ({ ...item, id: Date.now().toString() + Math.random() })),
      drapes: (caseData.drapes || []).map(item => ({ ...item, id: Date.now().toString() + Math.random() })),
      dressings: (caseData.dressings || []).map(item => ({ ...item, id: Date.now().toString() + Math.random() })),
      supplies: (caseData.supplies || []).map(item => ({ ...item, id: Date.now().toString() + Math.random() }))
    };
    // Copy all canvas images (images2, images3, etc.)
    for (let i = 2; i <= 10; i++) {
      const field = `images${i}`;
      if (caseData[field]) {
        newCase[field] = copyImages(caseData[field]);
      }
    }
    setCurrentCase(newCase);
    setTotalCanvasPages(countCanvasPages(caseData));
    setCurrentCanvasPage(1);
    setShowHome(false);
    setSelectedSpecialty(null);
    setHasUnsavedChanges(true);
  };

  const handleSaveCase = async () => {
    if (currentCase) {
      try {
        await saveCase(currentCase);
        setHasUnsavedChanges(false);
        setShowSavedToast(true);
        setTimeout(() => setShowSavedToast(false), 2000);
      } catch (error) {
        console.error('Error saving case:', error);
      }
    }
  };

  const handleLeaveEditor = () => {
    if (hasUnsavedChanges) {
      setShowSaveDialog(true);
    } else {
      doLeaveEditor();
    }
  };

  const doLeaveEditor = () => {
    const specialty = currentCase?.specialty;
    setCurrentCase(null);
    setHasUnsavedChanges(false);
    setShowSaveDialog(false);

    // Check if this specialty has its own page
    const specialtyPages = ['Cardiovascular', 'ENT', 'Ophthalmology', 'Urology'];
    if (specialtyPages.includes(specialty)) {
      setCurrentPage(specialty.toLowerCase());
      setSelectedSpecialty(null);
    } else {
      setSelectedSpecialty(specialty);
      setCurrentPage('cases');
    }
  };

  const handleSaveAndLeave = async () => {
    await handleSaveCase();
    doLeaveEditor();
  };

  const handleDontSaveAndLeave = () => {
    doLeaveEditor();
  };

  const handleCancelLeave = () => {
    setShowSaveDialog(false);
  };

  const handleDeleteClick = (caseData) => {
    setCaseToDelete(caseData);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (caseToDelete) {
      await deleteCase(caseToDelete.id);
    }
    setShowDeleteDialog(false);
    setCaseToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setCaseToDelete(null);
  };

  const handleNavigation = (page) => {
    setCurrentPage(page);
    setSelectedSpecialty(null);
    setCurrentCase(null);
    setShowHome(true);
  };

  const handleImageUpload = (e, canvasNumber = 1) => {
    const files = Array.from(e.target.files);
    const imageField = getImageField(canvasNumber);

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const compressedSrc = await compressImage(event.target.result, 800, 0.75);
        setCurrentCase(prev => {
          const existingImages = prev[imageField] || [];
          const maxZ = existingImages.length > 0
            ? Math.max(...existingImages.map(img => img.zIndex || 1))
            : 0;

          const newImage = {
            id: Date.now().toString() + Math.random(),
            src: compressedSrc,
            x: 200,
            y: 150,
            width: 150,
            height: 150,
            rotation: 0,
            mirrored: false,
            zIndex: maxZ + 1
          };

          return {
            ...prev,
            [imageField]: [...existingImages, newImage]
          };
        });
        setHasUnsavedChanges(true);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleMouseDown = (e, imageId, canvasNumber = 1) => {
    if (e.target.classList.contains('resize-handle')) return;
    
    const imageField = getImageField(canvasNumber);
    const image = currentCase[imageField].find(img => img.id === imageId);
    const rect = e.currentTarget.getBoundingClientRect();
    setDraggingImage({ id: imageId, canvas: canvasNumber });
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    e.stopPropagation();
  };

  const handleResizeStart = (e, imageId, canvasNumber = 1) => {
    const imageField = getImageField(canvasNumber);
    const image = currentCase[imageField].find(img => img.id === imageId);
    setResizingImage({ id: imageId, canvas: canvasNumber });
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: image.width,
      height: image.height
    });
    e.stopPropagation();
  };

  const handleMouseMove = (e) => {
    if (draggingImage) {
      const canvas = document.getElementById('surgical-canvas');
      if (!canvas) return;

      const imageField = getImageField(draggingImage.canvas);
      const rect = canvas.getBoundingClientRect();
      const newX = e.clientX - rect.left - dragOffset.x;
      const newY = e.clientY - rect.top - dragOffset.y;

      setCurrentCase(prev => ({
        ...prev,
        [imageField]: (prev[imageField] || []).map(img =>
          img.id === draggingImage.id
            ? { ...img, x: Math.max(10, Math.min(790 - img.width, newX)), y: Math.max(10, Math.min(590 - img.height, newY)) }
            : img
        )
      }));
    } else if (resizingImage) {
      const imageField = getImageField(resizingImage.canvas);
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      const newWidth = Math.max(50, resizeStart.width + deltaX);
      const newHeight = Math.max(50, resizeStart.height + deltaY);

      setCurrentCase(prev => ({
        ...prev,
        [imageField]: (prev[imageField] || []).map(img =>
          img.id === resizingImage.id
            ? { ...img, width: Math.min(400, newWidth), height: Math.min(400, newHeight) }
            : img
        )
      }));
    } else if (rotatingImage) {
      const imageField = getImageField(rotatingImage.canvas);

      // Calculate current angle from mouse position to center
      const currentAngle = Math.atan2(
        e.clientY - rotatingImage.centerY,
        e.clientX - rotatingImage.centerX
      ) * (180 / Math.PI);

      // Calculate rotation delta and apply to starting rotation
      const angleDelta = currentAngle - rotateStart.startAngle;
      let newRotation = rotateStart.angle + angleDelta;

      // Normalize to 0-360
      while (newRotation < 0) newRotation += 360;
      while (newRotation >= 360) newRotation -= 360;

      setCurrentCase(prev => ({
        ...prev,
        [imageField]: (prev[imageField] || []).map(img =>
          img.id === rotatingImage.id
            ? { ...img, rotation: newRotation }
            : img
        )
      }));
    }
  };

  const handleMouseUp = () => {
    if (draggingImage || resizingImage || rotatingImage) {
      setHasUnsavedChanges(true);
    }
    setDraggingImage(null);
    setResizingImage(null);
    setRotatingImage(null);
  };

  const handleDeleteImage = (imageId, canvasNumber = 1) => {
    console.log('DELETE IMAGE CALLED:', imageId);
    const imageField = getImageField(canvasNumber);
    setCurrentCase(prev => ({
      ...prev,
      [imageField]: (prev[imageField] || []).filter(img => img.id !== imageId)
    }));
    setHasUnsavedChanges(true);
  };

  const handleAddItem = (category) => {
    setCurrentCase(prev => ({
      ...prev,
      [category]: [...(prev[category] || []), { id: Date.now().toString(), catalogNumber: '', description: '', quantity: '', notes: '' }]
    }));
    setHasUnsavedChanges(true);
  };

  const handleUpdateItem = (category, itemId, field, value) => {
    setCurrentCase(prev => ({
      ...prev,
      [category]: (prev[category] || []).map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
    setHasUnsavedChanges(true);
  };

  const handleDeleteItem = (category, itemId) => {
    setCurrentCase(prev => ({
      ...prev,
      [category]: (prev[category] || []).filter(item => item.id !== itemId)
    }));
    setHasUnsavedChanges(true);
  };

  const handleRotateStart = (e, imageId, canvasNumber = 1) => {
    e.stopPropagation();
    const imageField = getImageField(canvasNumber);
    const image = currentCase[imageField].find(img => img.id === imageId);

    // Get the center of the image
    const canvasId = canvasNumber === 1 ? 'surgical-canvas' : 'surgical-canvas-2';
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const centerX = rect.left + image.x + image.width / 2;
    const centerY = rect.top + image.y + image.height / 2;

    // Calculate starting angle from mouse position
    const startAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);

    setRotatingImage({ id: imageId, canvas: canvasNumber, centerX, centerY });
    setRotateStart({ angle: image.rotation || 0, startAngle });
  };

  const handleMirrorImage = (imageId, canvasNumber = 1) => {
    const imageField = getImageField(canvasNumber);
    setCurrentCase(prev => ({
      ...prev,
      [imageField]: (prev[imageField] || []).map(img =>
        img.id === imageId ? { ...img, mirrored: !img.mirrored } : img
      )
    }));
    setHasUnsavedChanges(true);
  };

  const handleBringForward = (imageId, canvasNumber = 1) => {
    const imageField = getImageField(canvasNumber);
    setCurrentCase(prev => {
      const images = [...(prev[imageField] || [])];
      const currentImage = images.find(img => img.id === imageId);

      if (!currentImage) return prev;

      const currentZ = currentImage.zIndex || 1;

      // Sort all images by z-index to find position
      const sortedImages = [...images].sort((a, b) => (a.zIndex || 1) - (b.zIndex || 1));
      const currentPosition = sortedImages.findIndex(img => img.id === imageId);

      if (currentPosition >= sortedImages.length - 1) {
        // Already at top, do nothing
        return prev;
      }

      // Get the image above in the stack
      const swapWith = sortedImages[currentPosition + 1];
      const swapZ = swapWith.zIndex || 1;

      // If z-indexes are the same, increase current by 1
      const newCurrentZ = currentZ === swapZ ? swapZ + 1 : swapZ;
      const newSwapZ = currentZ === swapZ ? currentZ : currentZ;

      // Swap z-index values
      const updatedImages = images.map(img => {
        if (img.id === imageId) {
          return { ...img, zIndex: newCurrentZ };
        }
        if (img.id === swapWith.id) {
          return { ...img, zIndex: newSwapZ };
        }
        return img;
      });

      return {
        ...prev,
        [imageField]: updatedImages
      };
    });
    setHasUnsavedChanges(true);
  };

  const handleSendBackward = (imageId, canvasNumber = 1) => {
    const imageField = getImageField(canvasNumber);
    setCurrentCase(prev => {
      const images = [...(prev[imageField] || [])];
      const currentImage = images.find(img => img.id === imageId);

      if (!currentImage) return prev;

      const currentZ = currentImage.zIndex || 1;

      // Sort all images by z-index to find position
      const sortedImages = [...images].sort((a, b) => (a.zIndex || 1) - (b.zIndex || 1));
      const currentPosition = sortedImages.findIndex(img => img.id === imageId);

      if (currentPosition <= 0) {
        // Already at bottom, do nothing
        return prev;
      }

      // Get the image below in the stack
      const swapWith = sortedImages[currentPosition - 1];
      const swapZ = swapWith.zIndex || 1;

      // If z-indexes are the same, decrease current by 1
      const newCurrentZ = currentZ === swapZ ? swapZ - 1 : swapZ;
      const newSwapZ = currentZ === swapZ ? currentZ : currentZ;

      // Swap z-index values
      const updatedImages = images.map(img => {
        if (img.id === imageId) {
          return { ...img, zIndex: newCurrentZ };
        }
        if (img.id === swapWith.id) {
          return { ...img, zIndex: newSwapZ };
        }
        return img;
      });

      return {
        ...prev,
        [imageField]: updatedImages
      };
    });
    setHasUnsavedChanges(true);
  };

  const getFilteredCases = () => {
    let filtered = cases;
    if (selectedSpecialty) {
      filtered = filtered.filter(c => c.specialty === selectedSpecialty);
    }
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.surgeon.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.procedure.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.specialty.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  };

  if (loading) return null;
  if (!user) { window.location.replace('/coming-soon.html'); return null; }

  // PICKING CARDS PAGE
  if (currentPage === 'picking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-100">
        <NavBar currentPage={currentPage} onNavigate={handleNavigation} role={role} user={user} onLogout={logout} />
        <PickingCards onBack={() => setCurrentPage('home')} />
      </div>
    );
  }

  // MATERIALS MANAGEMENT PAGE
  if (currentPage === 'materials') {
    return (
      <div className="min-h-screen bg-slate-900">
        <NavBar currentPage={currentPage} onNavigate={handleNavigation} role={role} user={user} onLogout={logout} />
        <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <h1 className="text-3xl font-bold text-slate-100">Materials Management</h1>
              </div>
            </div>
            <div className="text-center py-12">
              <Package size={64} className="mx-auto mb-4 text-amber-500" />
              <p className="text-slate-500 text-lg">Materials Management coming soon...</p>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // CARDIOVASCULAR PAGE
  if (currentPage === 'cardiovascular') {
    const cardiovascularCases = cases.filter(c => c.specialty === 'Cardiovascular');
    return (
      <div className="min-h-screen bg-slate-900">
        <NavBar currentPage={currentPage} onNavigate={handleNavigation} role={role} user={user} onLogout={logout} />
        <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-rose-600 rounded-xl flex items-center justify-center">
                  <Heart size={24} className="text-white" />
                </div>
                <h1 className="text-3xl font-bold text-slate-100">Cardiovascular Cases</h1>
              </div>
              <button
                onClick={() => handleCreateCase('Cardiovascular')}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
              >
                <Plus size={20} /> New Case
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cardiovascularCases.map((caseData) => (
              <div key={caseData.id} className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden hover:border-slate-500 transition-colors">
                <div
                  className="w-full h-48 bg-slate-700 flex items-center justify-center relative overflow-hidden cursor-pointer"
                  onClick={() => {
                    setCurrentCase(caseData);
                    setTotalCanvasPages(countCanvasPages(caseData));
                    setCurrentCanvasPage(1);
                    setCurrentPage('');
                    setHasUnsavedChanges(false);
                  }}
                >
                  {caseData.images && caseData.images.length > 0 ? (
                    <div className="relative bg-gradient-to-b from-slate-700 to-slate-800" style={{ width: '100%', height: '100%' }}>
                      {[...caseData.images].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)).map((img) => (
                        <img
                          key={img.id}
                          src={img.src}
                          alt="Case"
                          className="absolute object-contain"
                          style={{
                            left: `${(img.x / 800) * 100}%`,
                            top: `${(img.y / 600) * 100}%`,
                            width: `${(img.width / 800) * 100}%`,
                            height: `${(img.height / 600) * 100}%`,
                            zIndex: img.zIndex || 0,
                            transform: `rotate(${img.rotation || 0}deg) scaleX(${img.mirrored ? -1 : 1})`
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <Image size={48} className="text-slate-500" />
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-xl font-bold text-slate-100 mb-1">{caseData.surgeon}</h3>
                  <p className="text-sm text-slate-400 mb-2">{caseData.procedure}</p>
                  <p className="text-xs text-slate-500 mb-3">{caseData.date}</p>
                  {caseData.notes && (
                    <p className="text-sm text-slate-300 mb-3 line-clamp-2">{caseData.notes}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setCurrentCase(caseData);
                        setTotalCanvasPages(countCanvasPages(caseData));
                        setCurrentCanvasPage(1);
                        setCurrentPage('');
                        setHasUnsavedChanges(false);
                      }}
                      className="flex-1 flex items-center justify-center gap-1 bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700 text-sm"
                    >
                      <Edit2 size={14} /> Edit
                    </button>
                    <button
                      onClick={() => handleCopyCase(caseData)}
                      className="flex-1 flex items-center justify-center gap-1 bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 text-sm"
                    >
                      <Copy size={14} /> Copy
                    </button>
                    <button
                      onClick={() => handleDeleteClick(caseData)}
                      className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {cardiovascularCases.length === 0 && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center">
              <Heart size={64} className="mx-auto mb-4 text-red-300" />
              <p className="text-slate-500 text-lg">No Cardiovascular cases found. Create your first one!</p>
            </div>
          )}
        </div>
        </div>
      </div>
    );
  }

  // ENT PAGE
  if (currentPage === 'ent') {
    return (
      <div className="min-h-screen bg-slate-900">
        <NavBar currentPage={currentPage} onNavigate={handleNavigation} role={role} user={user} onLogout={logout} />
        <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-xl flex items-center justify-center">
                  <Ear size={24} className="text-white" />
                </div>
                <h1 className="text-3xl font-bold text-slate-100">ENT</h1>
              </div>
            </div>
            <div className="text-center py-12">
              <Ear size={64} className="mx-auto mb-4 text-amber-500" />
              <p className="text-slate-500 text-lg">ENT coming soon...</p>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // OPHTHALMOLOGY PAGE
  if (currentPage === 'ophthalmology') {
    return (
      <div className="min-h-screen bg-slate-900">
        <NavBar currentPage={currentPage} onNavigate={handleNavigation} role={role} user={user} onLogout={logout} />
        <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-sky-600 rounded-xl flex items-center justify-center">
                  <Eye size={24} className="text-white" />
                </div>
                <h1 className="text-3xl font-bold text-slate-100">Ophthalmology</h1>
              </div>
              <button
                onClick={() => handleCreateCase('Ophthalmology')}
                className="flex items-center gap-2 bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700"
              >
                <Plus size={20} /> New Case
              </button>
            </div>
            <div className="text-center py-12">
              <Eye size={64} className="mx-auto mb-4 text-cyan-500" />
              <p className="text-slate-500 text-lg">Ophthalmology coming soon...</p>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // UROLOGY PAGE
  if (currentPage === 'urology') {
    return (
      <div className="min-h-screen bg-slate-900">
        <NavBar currentPage={currentPage} onNavigate={handleNavigation} role={role} user={user} onLogout={logout} />
        <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-violet-600 rounded-xl flex items-center justify-center">
                  <Activity size={24} className="text-white" />
                </div>
                <h1 className="text-3xl font-bold text-slate-100">Urology</h1>
              </div>
              <button
                onClick={() => handleCreateCase('Urology')}
                className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
              >
                <Plus size={20} /> New Case
              </button>
            </div>
            <div className="text-center py-12">
              <Activity size={64} className="mx-auto mb-4 text-indigo-500" />
              <p className="text-slate-500 text-lg">Urology coming soon...</p>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // MAIN HOME PAGE - App Selection
  if (currentPage === 'home' && showHome && !selectedSpecialty && !currentCase) {
    return (
      <div className="min-h-screen bg-slate-900">
        <NavBar currentPage={currentPage} onNavigate={handleNavigation} role={role} user={user} onLogout={logout} />
        <div className="p-6 flex flex-col justify-center" style={{ minHeight: 'calc(100vh - 56px)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-slate-100 mb-4">Welcome to SME</h1>
            <p className="text-xl text-slate-400">Surgical Management & Equipment</p>
          </div>

          {/* Main Navigation Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Surgical Case Cards */}
            <button
              onClick={() => setCurrentPage('cases')}
              className="group relative bg-slate-800 border border-slate-700 rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-600 opacity-10 group-hover:opacity-20 transition-opacity rounded-2xl" />
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform">
                <Scissors size={48} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-100 mb-2">Surgical Case Cards</h3>
              <p className="text-slate-400">Manage surgical cases by specialty</p>
              <p className="text-sm text-blue-600 mt-2">{cases.length} total cases</p>
            </button>

            {/* Picking Cards */}
            <button
              onClick={() => setCurrentPage('picking')}
              className="group relative bg-slate-800 border border-slate-700 rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-cyan-600 opacity-10 group-hover:opacity-20 transition-opacity rounded-2xl" />
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-teal-400 to-cyan-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform">
                <ClipboardList size={48} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-100 mb-2">Picking Cards</h3>
              <p className="text-slate-400">Track supplies and equipment picking</p>
            </button>

            {/* Materials Management */}
            <button
              onClick={() => setCurrentPage('materials')}
              className="group relative bg-slate-800 border border-slate-700 rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-600 opacity-10 group-hover:opacity-20 transition-opacity rounded-2xl" />
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-amber-400 to-orange-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform">
                <Package size={48} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-100 mb-2">Materials Management</h3>
              <p className="text-slate-400">Manage inventory and supplies</p>
            </button>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // SPECIALTY SELECTION PAGE
  if (currentPage === 'cases' && showHome && !selectedSpecialty && !currentCase) {
    return (
      <div className="min-h-screen bg-slate-900">
        <NavBar currentPage={currentPage} onNavigate={handleNavigation} role={role} user={user} onLogout={logout} />
        <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-slate-100 mb-4">Surgical Case Cards</h1>
            <p className="text-xl text-slate-400">Select a surgical specialty to begin</p>
          </div>

          <div className="mb-8 max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-4 text-slate-500" size={24} />
              <input
                type="text"
                placeholder="Search cases by surgeon, procedure, or specialty..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-14 pr-4 py-4 text-lg border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-lg"
              />
            </div>
          </div>

          {/* Show search results if searching */}
          {searchTerm && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-slate-100 mb-4">Search Results ({getFilteredCases().length} cases)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getFilteredCases().map((caseData) => (
                  <div key={caseData.id} className="bg-slate-800 border border-slate-700 rounded-lg p-4 hover:border-slate-500 transition-colors cursor-pointer"
                    onClick={() => {
                      setCurrentCase(caseData);
                      setTotalCanvasPages(countCanvasPages(caseData));
                      setCurrentCanvasPage(1);
                      setSelectedSpecialty(null);
                      setSearchTerm('');
                      setHasUnsavedChanges(false);
                    }}
                  >
                    <h3 className="font-bold text-slate-100">{caseData.surgeon}</h3>
                    <p className="text-sm text-slate-400">{caseData.procedure}</p>
                    <p className="text-xs text-slate-500">{caseData.specialty} • {caseData.date}</p>
                  </div>
                ))}
              </div>
              {getFilteredCases().length === 0 && (
                <p className="text-slate-500 text-center py-4">No cases found matching "{searchTerm}"</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {SPECIALTIES.map((specialty) => {
              const Icon = specialty.icon;
              const caseCount = cases.filter(c => c.specialty === specialty.name).length;

              return (
                <button
                  key={specialty.name}
                  onClick={() => {
                    if (specialty.isPage) {
                      setCurrentPage(specialty.name.toLowerCase());
                    } else {
                      setSelectedSpecialty(specialty.name);
                    }
                  }}
                  className="group relative bg-slate-800 border border-slate-700 rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${specialty.color} opacity-10 group-hover:opacity-20 transition-opacity rounded-2xl`} />
                  <div className={`w-20 h-20 mx-auto mb-4 bg-gradient-to-br ${specialty.color} rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform`}>
                    <Icon size={40} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-100 mb-2">{specialty.name}</h3>
                  <p className="text-sm text-slate-400">{caseCount} {caseCount === 1 ? 'case' : 'cases'}</p>
                </button>
              );
            })}
          </div>
        </div>
        </div>
      </div>
    );
  }

  // CASE LIST VIEW - for selected specialty
  if (selectedSpecialty && !currentCase) {
    const filteredCases = getFilteredCases();
    const specialty = SPECIALTIES.find(s => s.name === selectedSpecialty);
    const Icon = specialty.icon;

    return (
      <div className="min-h-screen bg-slate-900">
        <NavBar currentPage={currentPage} onNavigate={handleNavigation} role={role} user={user} onLogout={logout} />
        <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setSelectedSpecialty(null)}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  Back to Specialties
                </button>
                <div className={`w-12 h-12 bg-gradient-to-br ${specialty.color} rounded-xl flex items-center justify-center`}>
                  <Icon size={24} className="text-white" />
                </div>
                <h1 className="text-3xl font-bold text-slate-100">{selectedSpecialty} Cases</h1>
              </div>
              <button
                onClick={() => handleCreateCase(selectedSpecialty)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                <Plus size={20} /> New Case
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-3 text-slate-500" size={20} />
              <input
                type="text"
                placeholder="Search by surgeon or procedure..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-600 rounded-lg focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100 placeholder-slate-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCases.map((caseData) => (
              <div key={caseData.id} className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden hover:border-slate-500 transition-colors">
                <div
                  className="w-full h-48 bg-slate-700 flex items-center justify-center relative overflow-hidden cursor-pointer"
                  onClick={() => {
                    setCurrentCase(caseData);
                    setTotalCanvasPages(countCanvasPages(caseData));
                    setCurrentCanvasPage(1);
                    setSelectedSpecialty(null);
                    setHasUnsavedChanges(false);
                  }}
                >
                  {caseData.images.length > 0 ? (
                    <div
                      className="relative bg-gradient-to-b from-slate-700 to-slate-800"
                      style={{
                        width: '100%',
                        height: '100%',
                      }}
                    >
                      {[...caseData.images].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)).map((img) => (
                        <img
                          key={img.id}
                          src={img.src}
                          alt="Case"
                          className="absolute object-contain"
                          style={{
                            left: `${(img.x / 800) * 100}%`,
                            top: `${(img.y / 600) * 100}%`,
                            width: `${(img.width / 800) * 100}%`,
                            height: `${(img.height / 600) * 100}%`,
                            zIndex: img.zIndex || 0,
                            transform: `rotate(${img.rotation || 0}deg) scaleX(${img.mirrored ? -1 : 1})`
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <Image size={48} className="text-slate-500" />
                  )}
                </div>
                
                <div className="p-4">
                  <h3 className="text-xl font-bold text-slate-100 mb-1">{caseData.surgeon}</h3>
                  <p className="text-sm text-slate-400 mb-2">{caseData.procedure}</p>
                  <p className="text-xs text-slate-500 mb-3">{caseData.date}</p>
                  
                  {caseData.notes && (
                    <p className="text-sm text-slate-300 mb-3 line-clamp-2">{caseData.notes}</p>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setCurrentCase(caseData);
                        setTotalCanvasPages(countCanvasPages(caseData));
                        setCurrentCanvasPage(1);
                        setSelectedSpecialty(null);
                        setHasUnsavedChanges(false);
                      }}
                      className="flex-1 flex items-center justify-center gap-1 bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm"
                    >
                      <Edit2 size={14} /> Edit
                    </button>
                    <button
                      onClick={() => handleCopyCase(caseData)}
                      className="flex-1 flex items-center justify-center gap-1 bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 text-sm"
                    >
                      <Copy size={14} /> Copy
                    </button>
                    <button
                      onClick={() => handleDeleteClick(caseData)}
                      className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Delete Confirmation Dialog */}
          {showDeleteDialog && caseToDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4">
                <h3 className="text-xl font-bold text-slate-100 mb-4">Delete Case Card</h3>
                <p className="text-slate-400 mb-2">Are you sure you want to delete this case?</p>
                <div className="bg-slate-900 rounded-lg p-3 mb-6">
                  <p className="font-medium text-slate-100">{caseToDelete.surgeon}</p>
                  <p className="text-sm text-slate-400">{caseToDelete.procedure}</p>
                  <p className="text-xs text-slate-500">{caseToDelete.date}</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleCancelDelete}
                    className="flex-1 flex items-center justify-center gap-2 bg-gray-200 text-slate-300 px-4 py-3 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    <X size={18} /> Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 font-medium"
                  >
                    <Trash2 size={18} /> Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {filteredCases.length === 0 && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center">
              <p className="text-slate-500 text-lg">No cases found. Create your first {selectedSpecialty} case!</p>
            </div>
          )}
        </div>
        </div>
      </div>
    );
  }

  // CASE EDITOR VIEW
  if (currentCase) {
    return (
      <div className="min-h-screen bg-slate-900">
        <NavBar currentPage={currentPage} onNavigate={handleNavigation} role={role} user={user} onLogout={logout} />
        {/* Save Dialog Modal */}
        {showSaveDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-slate-100 mb-4">Unsaved Changes</h3>
              <p className="text-slate-400 mb-6">You have unsaved changes. What would you like to do?</p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleSaveAndLeave}
                  className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 font-medium"
                >
                  <Save size={18} /> Save Changes
                </button>
                <button
                  onClick={handleDontSaveAndLeave}
                  className="w-full flex items-center justify-center gap-2 bg-red-500 text-white px-4 py-3 rounded-lg hover:bg-red-600 font-medium"
                >
                  <Trash2 size={18} /> Don't Save
                </button>
                <button
                  onClick={handleCancelLeave}
                  className="w-full flex items-center justify-center gap-2 bg-gray-200 text-slate-300 px-4 py-3 rounded-lg hover:bg-gray-300 font-medium"
                >
                  <X size={18} /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-slate-900 border-b border-slate-800 p-4 flex justify-between items-center">
          <button
            onClick={handleLeaveEditor}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <X size={20} /> Back to {currentCase.specialty} Cases
          </button>
          <div className="relative flex items-center">
            {showSavedToast && (
              <span className="absolute -top-9 left-1/2 -translate-x-1/2 bg-green-600 text-white text-sm px-3 py-1 rounded-lg shadow whitespace-nowrap">
                Case saved!
              </span>
            )}
            <button
              onClick={handleSaveCase}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <Save size={18} /> Save Case
            </button>
          </div>
        </div>

        {/* Surgical Room Canvas */}
        <div className="p-6 flex justify-center">
          <div className="relative">
            {/* Main canvas area - fixed width to prevent shifting */}
            <div>
              <div className="flex justify-between items-end mb-3" style={{ width: '916px' }}>
                {/* Left side - Upload Images and Add Canvas buttons */}
                <div className="flex items-center gap-2" style={{ marginLeft: '74px' }}>
                  <label className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 cursor-pointer text-sm">
                    <Upload size={16} /> Upload Images
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleImageUpload(e, currentCanvasPage)}
                      className="hidden"
                    />
                  </label>
                  <button
                    onClick={() => {
                      setTotalCanvasPages(prev => prev + 1);
                      setCurrentCanvasPage(totalCanvasPages + 1);
                      setHasUnsavedChanges(true);
                    }}
                    className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm"
                    title="Add New Canvas"
                  >
                    <Plus size={16} /> Add Canvas
                  </button>
                </div>
                {/* Right side - Image Library button */}
                <div className="flex items-center gap-2 mr-14">
                  <button
                    onClick={() => {
                      setShowImageLibrary(!showImageLibrary);
                      if (!showImageLibrary) {
                        setSelectedLibraryCategory(null);
                        setImageLibrary([]);
                      }
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${showImageLibrary ? 'bg-purple-700 text-white' : 'bg-purple-600 text-white hover:bg-purple-700'}`}
                    title="Image Library"
                  >
                    <Grid size={16} /> Image Library
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Left Arrow */}
                <button
                  onClick={() => setCurrentCanvasPage(prev => Math.max(1, prev - 1))}
                  disabled={currentCanvasPage === 1}
                  className={`p-3 rounded-full shadow-lg ${currentCanvasPage === 1 ? 'bg-gray-300 text-slate-500 cursor-not-allowed' : 'bg-gray-700 text-white hover:bg-gray-800'}`}
                  title="Previous Page"
                >
                  <ChevronLeft size={28} />
                </button>

                <div
                  id="surgical-canvas"
                  className={`relative bg-gradient-to-b from-slate-700 to-slate-800 ${draggingLibraryImage ? 'ring-4 ring-purple-400' : ''}`}
                style={{
                  width: '800px',
                  height: '600px',
                  border: '8px solid #2c3e50',
                  boxShadow: 'inset 0 0 30px rgba(0,0,0,0.1), 0 10px 30px rgba(0,0,0,0.3)',
                  borderRadius: '4px'
                }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onDrop={handleCanvasDrop}
                onDragOver={handleCanvasDragOver}
              >
              {/* Floor grid pattern */}
              <div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
                  backgroundSize: '50px 50px',
                  zIndex: 0,
                  pointerEvents: 'none'
                }}
              />

              {(currentCase[getImageField(currentCanvasPage)] || []).length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center text-slate-500">
                  <div className="text-center">
                    <Image size={64} className="mx-auto mb-2" />
                    <p>Upload images or drag from library</p>
                    <p className="text-sm mt-1">Drag to move • Resize from corner</p>
                  </div>
                </div>
              )}

              {(currentCase[getImageField(currentCanvasPage)] || []).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)).map((image) => {
                const isActive = draggingImage?.id === image.id || resizingImage?.id === image.id || rotatingImage?.id === image.id;
                return (
                <div
                  key={image.id}
                  style={{
                    position: 'absolute',
                    left: image.x,
                    top: image.y,
                    width: image.width,
                    height: image.height,
                    zIndex: isActive ? 9999 : (image.zIndex || 0),
                    cursor: draggingImage?.id === image.id ? 'grabbing' : 'grab'
                  }}
                  onMouseDown={(e) => handleMouseDown(e, image.id, currentCanvasPage)}
                  className="group"
                >
                  <img
                    src={image.src}
                    alt="Surgical"
                    className="w-full h-full object-contain rounded-lg shadow-xl"
                    draggable="false"
                    style={{
                      pointerEvents: 'none',
                      imageRendering: 'high-quality',
                      transform: `rotate(${image.rotation || 0}deg) scaleX(${image.mirrored ? -1 : 1})`
                    }}
                  />

                  {/* Delete button - top right */}
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Delete this image?')) {
                        handleDeleteImage(image.id, currentCanvasPage);
                      }
                    }}
                    className={`absolute -top-3 -right-3 bg-red-500 text-white p-1.5 rounded-full transition-opacity shadow-lg z-10 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    title="Delete"
                  >
                    <X size={16} />
                  </button>

                  {/* Rotate handle - above mirror (top center, higher) */}
                  <div
                    onMouseDown={(e) => handleRotateStart(e, image.id, currentCanvasPage)}
                    className={`absolute -top-10 left-1/2 transform -translate-x-1/2 bg-purple-500 text-white p-1.5 rounded-full transition-opacity shadow-lg z-10 hover:bg-purple-600 cursor-grab active:cursor-grabbing ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    title="Drag to rotate"
                  >
                    <RotateCw size={16} />
                  </div>

                  {/* Mirror button - top center */}
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMirrorImage(image.id, currentCanvasPage);
                    }}
                    className={`absolute -top-3 left-1/2 transform -translate-x-1/2 bg-indigo-500 text-white p-1.5 rounded-full transition-opacity shadow-lg z-10 hover:bg-indigo-600 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    title="Mirror"
                  >
                    <FlipHorizontal size={16} />
                  </button>

                  {/* Bring forward button - top left */}
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBringForward(image.id, currentCanvasPage);
                    }}
                    className={`absolute -top-3 -left-3 bg-green-500 text-white p-1.5 rounded-full transition-opacity shadow-lg z-10 hover:bg-green-600 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    title="Bring Forward"
                  >
                    <ArrowUp size={16} />
                  </button>

                  {/* Send backward button - left center */}
                  <button
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      console.log('Send backward clicked for image:', image.id);
                      handleSendBackward(image.id, currentCanvasPage);
                    }}
                    className={`absolute -left-3 top-1/2 transform -translate-y-1/2 bg-orange-500 text-white p-1.5 rounded-full transition-opacity shadow-lg z-10 hover:bg-orange-600 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    title="Send Backward"
                  >
                    <ArrowDown size={16} />
                  </button>

                  {/* Resize handle - bottom right */}
                  <div
                    className={`resize-handle absolute -bottom-3 -right-3 bg-blue-500 text-white p-1.5 rounded-full transition-opacity cursor-nwse-resize shadow-lg z-10 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      handleResizeStart(e, image.id, currentCanvasPage);
                    }}
                    title="Resize"
                  >
                    <Maximize2 size={16} />
                  </div>
                </div>
              );
              })}
              </div>

                {/* Right Arrow */}
                <button
                  onClick={() => setCurrentCanvasPage(prev => Math.min(totalCanvasPages, prev + 1))}
                  disabled={currentCanvasPage === totalCanvasPages}
                  className={`p-3 rounded-full shadow-lg ${currentCanvasPage === totalCanvasPages ? 'bg-gray-300 text-slate-500 cursor-not-allowed' : 'bg-gray-700 text-white hover:bg-gray-800'}`}
                  title="Next Page"
                >
                  <ChevronRight size={28} />
                </button>
              </div>

              {/* Page Indicator Dots */}
              <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: totalCanvasPages }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentCanvasPage(pageNum)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      currentCanvasPage === pageNum
                        ? 'bg-blue-600 scale-125'
                        : 'bg-gray-400 hover:bg-gray-500'
                    }`}
                    title={`Page ${pageNum}`}
                  />
                ))}
              </div>
            </div>

            {/* Image Library Panel - Right of Canvas */}
            {showImageLibrary && (
              <div className="absolute top-0 bg-slate-800 border border-slate-700 rounded-lg p-4 border border-purple-200" style={{ width: '350px', left: '100%', marginLeft: '16px' }}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
                    <FolderOpen size={20} className="text-purple-600" />
                    Image Library
                  </h3>
                  <label className="flex items-center gap-1 bg-purple-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-purple-700 cursor-pointer">
                    <Upload size={14} /> Upload
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleUploadToLibrary}
                      className="hidden"
                    />
                  </label>
                </div>

                {/* Category tabs in a row */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {['Position Equipment', 'Tables', 'Machines', 'Prepping', 'Set-Ups'].map((category) => (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedLibraryCategory(category);
                        loadImageLibrary(category);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                        selectedLibraryCategory === category
                          ? 'bg-purple-600 text-white'
                          : 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>

                {/* Image grid - larger area */}
                {selectedLibraryCategory ? (
                  <div>
                    {imageLibrary.length === 0 ? (
                      <div className="text-center py-12 text-slate-400 bg-slate-900 rounded-lg">
                        <Image size={48} className="mx-auto mb-2 opacity-50" />
                        <p>No images in {selectedLibraryCategory}</p>
                        <p className="text-sm">Upload images to get started</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-3 max-h-96 overflow-y-auto p-1">
                        {imageLibrary.map((image) => (
                          <div
                            key={image.id}
                            className="relative group cursor-grab active:cursor-grabbing"
                            draggable
                            onDragStart={(e) => handleLibraryDragStart(e, image)}
                            onDragEnd={() => setDraggingLibraryImage(null)}
                          >
                            <img
                              src={image.thumbnail || image.src}
                              alt={image.name}
                              className="w-full h-24 object-cover rounded-lg border-2 border-slate-600 hover:border-purple-400 transition-all"
                              draggable="false"
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteLibraryImage(image.id);
                              }}
                              className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                              title="Delete"
                            >
                              <X size={12} />
                            </button>
                            <p className="text-xs text-center text-slate-200 mt-1 truncate" title={image.name}>
                              {image.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-3 text-center">
                      Drag images to the canvas
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500 bg-gray-50 rounded-lg">
                    <FolderOpen size={48} className="mx-auto mb-2 opacity-50" />
                    <p>Select a category above</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Case Notes Section Below */}
        <div className="p-6 max-w-4xl mx-auto">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-slate-100 mb-6">Case Notes</h2>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Surgeon Name</label>
                <input
                  type="text"
                  value={currentCase.surgeon}
                  onChange={(e) => { setCurrentCase({ ...currentCase, surgeon: e.target.value }); setHasUnsavedChanges(true); }}
                  className="w-full px-4 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Date</label>
                <input
                  type="date"
                  value={currentCase.date}
                  onChange={(e) => { setCurrentCase({ ...currentCase, date: e.target.value }); setHasUnsavedChanges(true); }}
                  className="w-full px-4 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">Procedure</label>
              <input
                type="text"
                value={currentCase.procedure}
                onChange={(e) => { setCurrentCase({ ...currentCase, procedure: e.target.value }); setHasUnsavedChanges(true); }}
                className="w-full px-4 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">Notes</label>
              <textarea
                value={currentCase.notes}
                onChange={(e) => { setCurrentCase({ ...currentCase, notes: e.target.value }); setHasUnsavedChanges(true); }}
                className="w-full px-4 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100"
                rows="6"
                placeholder="Enter detailed case notes, observations, complications, outcomes..."
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-slate-300">Medications/Solutions</label>
                <button
                  onClick={() => handleAddItem('meds')}
                  className="flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700"
                >
                  <Plus size={16} /> Add Medication
                </button>
              </div>
              <div className="space-y-3 mb-6">
                {(currentCase.meds || []).map((item) => (
                  <div key={item.id} className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                    <div className="grid grid-cols-12 gap-3">
                      <textarea
                        value={item.description}
                        onChange={(e) => {
                          handleUpdateItem('meds', item.id, 'description', e.target.value);
                          e.target.style.height = '42px';
                          e.target.style.height = Math.max(42, e.target.scrollHeight) + 'px';
                        }}
                        ref={(el) => { if (el) { el.style.height = '42px'; el.style.height = Math.max(42, el.scrollHeight) + 'px'; } }}
                        placeholder="Medication name (e.g., Cefazolin)"
                        className="col-span-5 px-3 py-2 border border-slate-600 rounded focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100 resize-none overflow-hidden"
                        rows="1"
                        style={{ minHeight: '42px' }}
                      />
                      <textarea
                        value={item.quantity}
                        onChange={(e) => {
                          handleUpdateItem('meds', item.id, 'quantity', e.target.value);
                          e.target.style.height = '42px';
                          e.target.style.height = Math.max(42, e.target.scrollHeight) + 'px';
                        }}
                        ref={(el) => { if (el) { el.style.height = '42px'; el.style.height = Math.max(42, el.scrollHeight) + 'px'; } }}
                        placeholder="Dose"
                        className="col-span-2 px-3 py-2 border border-slate-600 rounded focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100 resize-none overflow-hidden"
                        rows="1"
                        style={{ minHeight: '42px' }}
                      />
                      <textarea
                        value={item.notes}
                        onChange={(e) => {
                          handleUpdateItem('meds', item.id, 'notes', e.target.value);
                          e.target.style.height = '42px';
                          e.target.style.height = Math.max(42, e.target.scrollHeight) + 'px';
                        }}
                        ref={(el) => { if (el) { el.style.height = '42px'; el.style.height = Math.max(42, el.scrollHeight) + 'px'; } }}
                        placeholder="Notes"
                        className="col-span-4 px-3 py-2 border border-slate-600 rounded focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100 resize-none overflow-hidden"
                        rows="1"
                        style={{ minHeight: '42px' }}
                      />
                      <button
                        onClick={() => handleDeleteItem('meds', item.id)}
                        className="col-span-1 bg-red-500 text-white rounded hover:bg-red-600 flex items-center justify-center"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {[
              { key: 'trays', label: 'Trays/Instruments', placeholder: 'Tray name (e.g., Laparoscopic tray)' },
              { key: 'drapes', label: 'Drapes/Gown/Pack', placeholder: 'Item name (e.g., Laparotomy drape)' },
              { key: 'gloves', label: 'Gloves', placeholder: 'Glove size/type (e.g., 7.5 Sterile)' },
              { key: 'supplies', label: 'Supplies', placeholder: 'Item name (e.g., Foley catheter)' },
              { key: 'sutures', label: 'Sutures', placeholder: 'Suture type (e.g., 2-0 Vicryl)' },
              { key: 'dressings', label: 'Dressings', placeholder: 'Item name (e.g., Tegaderm)' },
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-slate-300">{label}</label>
                  <button
                    onClick={() => handleAddItem(key)}
                    className="flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700"
                  >
                    <Plus size={16} /> Add Item
                  </button>
                </div>
                <div className="space-y-3 mb-6">
                  {(currentCase[key] || []).map((item) => (
                    <div key={item.id} className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                      <div className="grid grid-cols-12 gap-3">
                        <textarea
                          value={item.catalogNumber || ''}
                          onChange={(e) => {
                            handleUpdateItem(key, item.id, 'catalogNumber', e.target.value);
                            e.target.style.height = '42px';
                            e.target.style.height = Math.max(42, e.target.scrollHeight) + 'px';
                          }}
                          ref={(el) => { if (el) { el.style.height = '42px'; el.style.height = Math.max(42, el.scrollHeight) + 'px'; } }}
                          placeholder="Catalog #"
                          className="col-span-2 px-3 py-2 border border-slate-600 rounded focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100 resize-none overflow-hidden"
                          rows="1"
                          style={{ minHeight: '42px' }}
                        />
                        <textarea
                          value={item.description}
                          onChange={(e) => {
                            handleUpdateItem(key, item.id, 'description', e.target.value);
                            e.target.style.height = '42px';
                            e.target.style.height = Math.max(42, e.target.scrollHeight) + 'px';
                          }}
                          ref={(el) => { if (el) { el.style.height = '42px'; el.style.height = Math.max(42, el.scrollHeight) + 'px'; } }}
                          placeholder={placeholder}
                          className="col-span-4 px-3 py-2 border border-slate-600 rounded focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100 resize-none overflow-hidden"
                          rows="1"
                          style={{ minHeight: '42px' }}
                        />
                        <textarea
                          value={item.quantity}
                          onChange={(e) => {
                            handleUpdateItem(key, item.id, 'quantity', e.target.value);
                            e.target.style.height = '42px';
                            e.target.style.height = Math.max(42, e.target.scrollHeight) + 'px';
                          }}
                          ref={(el) => { if (el) { el.style.height = '42px'; el.style.height = Math.max(42, el.scrollHeight) + 'px'; } }}
                          placeholder="Qty"
                          className="col-span-1 px-3 py-2 border border-slate-600 rounded focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100 resize-none overflow-hidden"
                          rows="1"
                          style={{ minHeight: '42px' }}
                        />
                        <textarea
                          value={item.notes}
                          onChange={(e) => {
                            handleUpdateItem(key, item.id, 'notes', e.target.value);
                            e.target.style.height = '42px';
                            e.target.style.height = Math.max(42, e.target.scrollHeight) + 'px';
                          }}
                          ref={(el) => { if (el) { el.style.height = '42px'; el.style.height = Math.max(42, el.scrollHeight) + 'px'; } }}
                          placeholder="Notes"
                          className="col-span-4 px-3 py-2 border border-slate-600 rounded focus:ring-2 focus:ring-teal-500 bg-slate-900 text-slate-100 resize-none overflow-hidden"
                          rows="1"
                          style={{ minHeight: '42px' }}
                        />
                        <button
                          onClick={() => handleDeleteItem(key, item.id)}
                          className="col-span-1 bg-red-500 text-white rounded hover:bg-red-600 flex items-center justify-center"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (currentPage === 'admin') {
    return <ProtectedRoute roles={['admin']}><AdminPage /></ProtectedRoute>;
  }

  // Fallback - reset to home page if no condition matched
  return (
    <div className="min-h-screen bg-slate-900 p-6 flex flex-col justify-center">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-slate-100 mb-4">Welcome to SME</h1>
          <p className="text-xl text-slate-400">Surgical Management & Equipment</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <button
            onClick={() => { setCurrentPage('cases'); setShowHome(true); setSelectedSpecialty(null); setCurrentCase(null); }}
            className="group relative bg-slate-800 border border-slate-700 rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-600 opacity-10 group-hover:opacity-20 transition-opacity rounded-2xl" />
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform">
              <Scissors size={48} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold text-slate-100 mb-2">Surgical Case Cards</h3>
            <p className="text-slate-400">Manage surgical cases by specialty</p>
          </button>

          <button
            onClick={() => setCurrentPage('picking')}
            className="group relative bg-slate-800 border border-slate-700 rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-cyan-600 opacity-10 group-hover:opacity-20 transition-opacity rounded-2xl" />
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-teal-400 to-cyan-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform">
              <ClipboardList size={48} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold text-slate-100 mb-2">Picking Cards</h3>
            <p className="text-slate-400">Track supplies and equipment picking</p>
          </button>

          <button
            onClick={() => setCurrentPage('materials')}
            className="group relative bg-slate-800 border border-slate-700 rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-600 opacity-10 group-hover:opacity-20 transition-opacity rounded-2xl" />
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-amber-400 to-orange-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform">
              <Package size={48} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold text-slate-100 mb-2">Materials Management</h3>
            <p className="text-slate-400">Manage inventory and supplies</p>
          </button>
        </div>
      </div>
    </div>
  );
}

function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export { App };
export default AppWithAuth;